import React, { useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { appWithTranslation, useTranslation } from "next-i18next";
import Head from "next/head";
import Router from "next/router";
import dynamic from "next/dynamic";
import "regenerator-runtime/runtime";

import nextI18NextConfig from "../../next-i18next.config.js";
import {
  AuthProvider,
  SubscriptionsProvider,
  HistoryProvider,
  useAuthContext,
  PagePropsProvider,
} from "@/contexts/index";
import { wrapper } from "@/store/index";
import { systemActions } from "@/store/actions/system.actions.js";
import { mainTreeActions } from "@/store/actions/mainTree.actions.js";
import { settingsActions } from "@/store/actions/settings.actions.js";
import { useRegulateTheme } from "@/hooks/useTheme";
import { ContextMenuProvider } from "@/contexts/righthClickMenuContext.js";
import { useDefineWebsiteUrlsForOfflineUse } from "@/hooks/sw/useSW";
import useCheckVersion from "@/shared/hooks/useCheckVersion";
import useDetectOnLine from "@/shared/hooks/useDetectOnLine";
import useAppHeight from "@/shared/hooks/useAppHeight";
import useCheckContentVersion from "@/shared/hooks/useCheckContentVersion";
import { AudioProvider } from "@/shared/contexts/AudioContext";
import {
  useAutoDownloadWebsite,
  useClearSWDataWhenVersionsDiffers,
  useHandleSWMessages,
  useRegisterServiceWorker,
  useStashInstallPWAPrompt,
} from "@/shared/hooks/pwa/sw";
import { useIsFontsLoaded } from "@/shared/hooks/useIsFontsLoaded";
import { CLASSNAME_IS_APP_INIT } from "@/utils/constants";
import useRegulateWhatInputClassNames from "@/shared/hooks/useRegulateWhatInputClassNames";
import Toaster from "@/shared/components/Toaster/Toaster";
import { TooltipProvider } from "@/shared/components/Tooltip/TooltipContext";
import { SearchProvider } from "@/contexts/search";
import useAddHTMLClassNameIsIOSDevice from "@/shared/hooks/useAddHTMLClassNameIsIOSDevice";
import {
  DATA_ACQUISITION_STATUS,
  minLoadingTimeInMillisecondsDefault,
} from "@/shared/components/Loader/Loader.constants.js";
import { _sharedActionsSystem } from "@/shared/store/system/actions.js";
import TextModePage from "@/shared/pages/TextModePage";
import ModalProvider from "@/shared/contexts/modal";
import { selectorSystemOnLine } from "@/shared/store/system/selectors";
import useRegulateBrowserZoom from "@/shared/hooks/useRegulateBrowserZoom";
import { WINDOW_PROP_KEY_TIMESTAMP_APP_HTML_PARSED } from "@/shared/pages/TextModePageScript.js";
import useIOSGlobalClassNames from "@/shared/hooks/useIOSGlobalClassNames";
import {
  selectorIsSWInit,
  selectorIsSWInitStatusRetrieved,
  selectorIsSWRegistered,
} from "@/shared/store";
import { SharedTranslationProvider } from "@/shared/contexts/TranslateContext";
import { SETTING } from "@/shared/utils/settings/constants.js";
import { NKJV_BIBLE } from "@/shared/utils/content/content.constants.js";

// dynamic, to prevent console errors - server content mismatch the client's.
const BadgeOffline = dynamic(() => import("@/shared/components/BadgeOffline/BadgeOffline"), {
  ssr: false,
});

const InstallPWANotification = dynamic(
  () => {
    return import("@/components/InstallPWANotification/InstallPWANotification");
  },
  {
    ssr: false,
  },
);

import "@/shared/what-input/what-input";
import "@/styles/global.scss";

const Content = ({ pageProps, Component }) => {
  const isLanding = pageProps.isLanding;
  const isSetCONFLICT = pageProps?.isSetCONFLICT;
  const book = pageProps.book || pageProps.mainPanel?.book;

  const dispatch = useDispatch();

  const onLine = useSelector(selectorSystemOnLine);
  const isSWInit = useSelector(selectorIsSWInit);
  const isSWRegistered = useSelector(selectorIsSWRegistered);
  const isSWInitStatusRetrieved = useSelector(selectorIsSWInitStatusRetrieved);

  const [loading, setLoading] = useState(false);

  useRegulateTheme(pageProps);
  useRegulateBrowserZoom();
  useRegulateWhatInputClassNames();
  useAddHTMLClassNameIsIOSDevice();
  useIOSGlobalClassNames();

  useRegisterServiceWorker("/sw.js");
  useDefineWebsiteUrlsForOfflineUse(isLanding, pageProps, book);
  useAutoDownloadWebsite(isLanding);
  useHandleSWMessages();
  useClearSWDataWhenVersionsDiffers();

  // Check versions only after the SW is available.
  // So, if the app version differs, the event could be sent to clean the cache.
  const isSWInitAndRegistered = isSWInit && isSWRegistered;
  useCheckContentVersion(isSWInitAndRegistered);
  useCheckVersion(isSWInitAndRegistered);

  useDetectOnLine();
  useAppHeight();

  // Update default bible for Conflict landing page
  useEffect(() => {
    if (isSetCONFLICT) {
      dispatch(
        settingsActions.updateSettings({
          [SETTING.baseBible.id]: NKJV_BIBLE,
          [SETTING.jtlBaseBible.id]: NKJV_BIBLE,
        }),
      );
    }
  }, [isSetCONFLICT]);

  // Regulates loading flag.
  useEffect(() => {
    dispatch(mainTreeActions.fetchMainTreeAllData());
    dispatch(settingsActions.fetchGeoIpData());

    let routePrev, localePrev;
    const start = () => {
      const { locale, route } = Router.router;

      const routeFinal = Router.router.route === "/" ? "/" : "/" + route.split("/")[1] + "/";

      routePrev = routeFinal;
      localePrev = locale;

      setLoading(true);
    };

    const end = (pathnameNew) => {
      setLoading(false);

      if (typeof pathnameNew === "string") {
        const isTheSameRoute =
          routePrev === "/"
            ? pathnameNew === "/" || "/" + localePrev + "/"
            : pathnameNew.startsWith(routePrev) ||
              pathnameNew.startsWith("/" + localePrev + routePrev);

        if (!isTheSameRoute) {
          dispatch(systemActions.actionHideAll());
        }
      }
    };

    Router.events.on("routeChangeStart", start);
    Router.events.on("routeChangeComplete", end);
    Router.events.on("routeChangeError", end);

    return () => {
      Router.events.off("routeChangeStart", start);
      Router.events.off("routeChangeComplete", end);
      Router.events.off("routeChangeError", end);
    };
  }, []);

  return (
    <PagePropsProvider {...pageProps}>
      <HistoryProvider>
        <SubscriptionsProvider>
          <SearchProvider>
            <AudioProvider analytics={false}>
              <ContextMenuProvider>
                {!onLine && <BadgeOffline />}
                <Component isLoading={loading} {...pageProps} />
                {!!(
                  isLanding &&
                  pageProps.isReader &&
                  book &&
                  isSWRegistered &&
                  isSWInitStatusRetrieved
                ) && (
                  <InstallPWANotification
                    isLanding={isLanding}
                    book={book}
                    booksSet={pageProps.booksSet}
                  />
                )}
                {!isLanding && <TextModePage />}
              </ContextMenuProvider>
            </AudioProvider>
          </SearchProvider>
        </SubscriptionsProvider>
      </HistoryProvider>
      <Toaster />
    </PagePropsProvider>
  );
};

const InitPage = ({ Component, pageProps }) => {
  const { isInit } = useAuthContext();
  const dispatch = useDispatch();
  const isFontsLoaded = useIsFontsLoaded();
  const isAppInit = isFontsLoaded && isInit;

  useStashInstallPWAPrompt();

  // Regulates loader overlay based on is fonts loading.
  useEffect(() => {
    if (isAppInit) {
      const delta = Date.now() - window[WINDOW_PROP_KEY_TIMESTAMP_APP_HTML_PARSED];
      if (delta > minLoadingTimeInMillisecondsDefault) {
        dispatch(_sharedActionsSystem.updateDataAcquisitionStatus(DATA_ACQUISITION_STATUS.LOW));
      }

      global.window.document.documentElement.classList.add(CLASSNAME_IS_APP_INIT);
    }
  }, [isAppInit]);

  return <Content Component={Component} pageProps={pageProps} />;
};

const App = ({ Component, ...restProps }) => {
  const { store, props } = wrapper.useWrappedStore(restProps);
  const { pageProps } = props;
  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <Provider store={store}>
        <SharedTranslationProvider useTranslation={useTranslation}>
          <TooltipProvider>
            <ModalProvider>
              <AuthProvider>
                <InitPage Component={Component} pageProps={pageProps} />
              </AuthProvider>
            </ModalProvider>
          </TooltipProvider>
        </SharedTranslationProvider>
      </Provider>
    </>
  );
};

export default appWithTranslation(App, nextI18NextConfig);
